import { getNumberSeparators } from '@/utils';
import { Stack, TextField, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { SourceInfo, NumericFormat, NumberFormatValues } from 'react-number-format';

type Props = {
  label?: string;
  labelFrom?: string;
  labelUntil?: string;
  name: string;
  prefix?: string;
  filters: Record<string, any>;
  onChange: (value: SetStateAction<any>) => void;
};

const DEBOUNCE_TIME: number = 400;

export const NumericRange: FC<Props> = ({
  label,
  labelFrom,
  labelUntil,
  name,
  prefix = 'USD ',
  filters,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const { decimalSeparator, thousandSeparator } = getNumberSeparators(i18n.language);

  if (!label) label = t('Number Range');
  if (!labelFrom) labelFrom = t('From');
  if (!labelUntil) labelUntil = t('Until');

  const handleRangeChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    const fieldName = (sourceInfo.event?.target as HTMLInputElement)?.name;
    if (!fieldName) return;

    const newValue = Number(values.floatValue ?? 0);
    onChange((prevState: Record<string, any>) => ({
      ...prevState,
      page: 0,
      [fieldName]: newValue,
      [name]: {
        ...(prevState[name] ?? {}),
        [fieldName.indexOf('From') !== -1 ? 'from' : 'until']: values,
      },
    }));
  };

  return (
    <Stack>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {label}
      </Typography>

      <Stack spacing={2.5} direction="row">
        <NumericFormat
          label={labelFrom}
          name={`${name}From`}
          value={filters?.[`${name}From`] ?? ''}
          customInput={TextField}
          allowNegative={false}
          decimalScale={0}
          prefix={prefix}
          thousandsGroupStyle="thousand"
          thousandSeparator={thousandSeparator}
          decimalSeparator={decimalSeparator}
          fixedDecimalScale
          onValueChange={debounce(handleRangeChange, DEBOUNCE_TIME)}
        />

        <NumericFormat
          label={labelUntil}
          name={`${name}Until`}
          value={filters?.[`${name}Until`] ?? ''}
          customInput={TextField}
          allowNegative={false}
          decimalScale={0}
          prefix={prefix}
          thousandsGroupStyle="thousand"
          thousandSeparator={thousandSeparator}
          decimalSeparator={decimalSeparator}
          fixedDecimalScale
          onValueChange={debounce(handleRangeChange, DEBOUNCE_TIME)}
        />
      </Stack>
    </Stack>
  );
};
